import { apiFetch } from '../../utils/apiFetch';
import { getCookie } from '../../utils/cookies';

const normalizeOrder = (order = {}) => {
  const rawNotes = String(order.notes ?? '');

  // Extract slot from all possible backend property names or notes
  let slotName =
    order.delivery_slot ||
    order.delivery_slot_name ||
    order.slot_name ||
    order.time_slot ||
    order.delivery_time_slot ||
    order.delivery_time ||
    order.slot ||
    '';

  // If not found in explicit fields, parse from notes if present
  if (!slotName && rawNotes.includes('Time slot:')) {
    const match = rawNotes.match(/Time slot:\s*([^|]+)/i);
    if (match && match[1]) {
      slotName = match[1].trim();
    }
  }

  // Determine delivery_type: check delivery_type, delivery_option, or infer from slot
  let resolvedDeliveryType = String(order.delivery_type ?? order.delivery_option ?? '').trim().toUpperCase();
  if (!resolvedDeliveryType || resolvedDeliveryType === 'STANDARD') {
    if (slotName || rawNotes.includes('Time slot:')) {
      resolvedDeliveryType = 'SCHEDULED';
    } else if (!resolvedDeliveryType) {
      resolvedDeliveryType = 'STANDARD';
    }
  }

  const isSched = resolvedDeliveryType === 'SCHEDULED';
  const deliveryFee = isSched ? 0 : parseFloat(order.delivery_fee ?? 0);

  // Clean notes so "Time slot: ..." doesn't clutter user notes display if separate slot display is shown
  let cleanNotes = rawNotes;
  if (cleanNotes.includes('Time slot:')) {
    cleanNotes = cleanNotes.replace(/Time slot:\s*[^|]+(\s*\|\s*)?/i, '').trim();
  }

  return {
    ...order,
    order_id:           order.order_id ?? order.id,
    order_number:       order.order_number ?? `#${order.order_id ?? order.id}`,
    status:             (order.order_status ?? order.status ?? 'PENDING').toUpperCase(),
    order_status:       (order.order_status ?? order.status ?? 'PENDING').toUpperCase(),
    payment_status:     (order.payment_status ?? 'UNPAID').toUpperCase(),
    payment_method:     (order.payment_method ?? 'COD').toUpperCase(),
    delivery_type:      resolvedDeliveryType,
    delivery_fee:       deliveryFee,
    delivery_slot_name: slotName || order.delivery_slot_name || null,
    slot_name:          slotName || order.slot_name || null,
    time_slot:          slotName || order.time_slot || null,
    delivery_slot:      slotName || order.delivery_slot || null,
    delivery_date:      order.delivery_date || order.scheduled_date || null,
    scheduled_date:     order.scheduled_date || order.delivery_date || null,
    clean_notes:        cleanNotes,
    notes:              order.notes,
    total:              parseFloat(order.total_amount ?? order.total ?? 0),
    total_amount:       parseFloat(order.total_amount ?? order.total ?? 0),
    placed_at:          order.placed_at ?? order.created_at ?? new Date().toISOString(),
    created_at:         order.created_at ?? order.placed_at ?? new Date().toISOString(),
    items:              order.items ?? [],
  };
};

export const createOrdersSlice = (set, get) => ({
  orders:              [],
  orders_loading:      false,
  orders_error:        null,
  orders_pagination:   { page: 1, total_pages: 1, total_orders: 0, limit: 10 },
  current_order:       null,
  place_order_loading: false,
  place_order_error:   null,
  order_tracking:      null,
  tracking_loading:    false,
  tracking_error:      null,

  // GET /api/v1/customer/orders
  fetchOrders: async ({ page = 1, limit = 10, status, silent = false } = {}) => {
    if (!silent) {
      set({ orders_loading: true, orders_error: null });
    }
    try {
      const params = new URLSearchParams({ page, limit });
      if (status) params.set('status', status);
      const json   = await apiFetch(`api/v1/customer/orders?${params}`);
      const orders = (json.data?.orders ?? json.data ?? [])
        .map(normalizeOrder)
        .sort((a, b) => {
          const dateA = new Date(a.created_at ?? a.placed_at ?? 0).getTime();
          const dateB = new Date(b.created_at ?? b.placed_at ?? 0).getTime();
          return dateB - dateA; // newest first
        });
      const pagination = {
        page:         json.data?.page         ?? json.data?.current_page  ?? page,
        total_pages:  json.data?.total_pages  ?? json.data?.last_page     ?? 1,
        total_orders: json.data?.total_orders ?? json.data?.total         ?? orders.length,
        limit,
      };
      set({ orders, orders_pagination: pagination, orders_loading: false });
      return orders;
    } catch (err) {
      if (!silent) {
        set({ orders_error: err.message, orders_loading: false });
      }
      return [];
    }
  },

  // Append next page to existing orders list
  fetchMoreOrders: async ({ status } = {}) => {
    const { orders_pagination, orders_loading } = get();
    if (orders_loading) return;
    const nextPage = (orders_pagination.page ?? 1) + 1;
    if (nextPage > orders_pagination.total_pages) return;
    set({ orders_loading: true, orders_error: null });
    try {
      const params = new URLSearchParams({ page: nextPage, limit: orders_pagination.limit ?? 10 });
      if (status) params.set('status', status);
      const json      = await apiFetch(`api/v1/customer/orders?${params}`);
      const newOrders = (json.data?.orders ?? json.data ?? []).map(normalizeOrder);
      const pagination = {
        page:         json.data?.page         ?? json.data?.current_page  ?? nextPage,
        total_pages:  json.data?.total_pages  ?? json.data?.last_page     ?? orders_pagination.total_pages,
        total_orders: json.data?.total_orders ?? json.data?.total         ?? orders_pagination.total_orders,
        limit:        orders_pagination.limit,
      };
      set((state) => ({
        orders: [...state.orders, ...newOrders].sort((a, b) => {
          const dateA = new Date(a.created_at ?? a.placed_at ?? 0).getTime();
          const dateB = new Date(b.created_at ?? b.placed_at ?? 0).getTime();
          return dateB - dateA; // newest first
        }),
        orders_pagination: pagination,
        orders_loading: false,
      }));
    } catch (err) {
      set({ orders_error: err.message, orders_loading: false });
    }
  },

  // POST /api/v1/customer/orders/place
  // Body: { address_id, payment_method, use_wallet?, delivery_type, notes? }
  placeOrder: async ({
    address_id,
    payment_method,
    notes = '',
    delivery_type = '',
    delivery_fee = null,
    delivery_slot_id = null,
    delivery_slot_name = null,
    time_slot = '',
    use_wallet = false,
    coupon_code = null,
    buyNowItem = null,
  }) => {
    const token = getCookie('authToken');
    if (!token) throw new Error('Please login to place an order');

    set({ place_order_loading: true, place_order_error: null });

    // ── Buy Now: swap server cart → single item → place → restore on fail ──
    let snapshotItems = null;
    if (buyNowItem) {
      try {
        const snap    = await apiFetch('api/v1/customer/cart');
        snapshotItems = snap.data?.items ?? snap.data?.cart_items ?? [];

        await apiFetch('api/v1/customer/cart/clear', { method: 'DELETE' });

        const addPayload = {
          product_id: Number(buyNowItem.product_id),
          quantity:   Number(buyNowItem.quantity),
        };
        if (buyNowItem.product_variant_id) addPayload.product_variant_id = Number(buyNowItem.product_variant_id);
        await apiFetch('api/v1/customer/cart/add', { method: 'POST', body: addPayload });
      } catch (err) {
        set({ place_order_error: err.message, place_order_loading: false });
        throw err;
      }
    }

    try {
      const isSched = String(delivery_type || '').toUpperCase() === 'SCHEDULED';
      const slotVal = String(delivery_slot_name || time_slot || '').trim();
      const orderBody = {
        address_id:     Number(address_id),
        payment_method,
        order_type:     'DELIVERY',
        delivery_type:  String(delivery_type || 'EXPRESS').toUpperCase(),
      };
      if (coupon_code) orderBody.coupon_code = String(coupon_code).trim().toUpperCase();

      if (delivery_fee != null || isSched) {
        orderBody.delivery_fee = isSched ? 0 : Number(delivery_fee);
      }
      if (delivery_slot_id != null) {
        orderBody.delivery_slot_id = Number(delivery_slot_id);
      }
      if (slotVal) {
        orderBody.delivery_slot_name = slotVal;
        orderBody.slot_name = slotVal;
        orderBody.time_slot = slotVal;
        orderBody.delivery_slot = slotVal;
      }

      let finalNotes = notes ? String(notes).trim() : '';
      if (isSched && slotVal && !finalNotes.includes(slotVal)) {
        finalNotes = finalNotes ? `Time slot: ${slotVal} | ${finalNotes}` : `Time slot: ${slotVal}`;
      }
      if (finalNotes) {
        orderBody.notes = finalNotes;
      }

      // Wallet: only include if the user opted in
      if (use_wallet === true) {
        orderBody.use_wallet = true;
      }

      const json = await apiFetch('api/v1/customer/orders/place', {
        method: 'POST',
        body: orderBody,
      });

      const rawOrder = json.data?.order ?? json.data ?? {};
      const orderItems = json.data?.items ?? rawOrder.items ?? [];
      const resolvedFee = isSched
        ? 0
        : (delivery_fee != null ? Number(delivery_fee) : rawOrder.delivery_fee);
      const resolvedSlot = slotVal || rawOrder.delivery_slot_name || rawOrder.slot_name || rawOrder.time_slot;

      const order = normalizeOrder({
        ...rawOrder,
        discount_amount: rawOrder.discount_amount ?? rawOrder.coupon_discount ?? 0,
        coupon_code: rawOrder.coupon_code ?? null,
        delivery_type: delivery_type ? String(delivery_type).toUpperCase() : (rawOrder.delivery_type || (resolvedSlot ? 'SCHEDULED' : 'STANDARD')),
        delivery_fee: resolvedFee != null ? resolvedFee : rawOrder.delivery_fee,
        delivery_slot_id: delivery_slot_id != null ? Number(delivery_slot_id) : rawOrder.delivery_slot_id,
        delivery_slot_name: resolvedSlot,
        slot_name: resolvedSlot,
        time_slot: resolvedSlot,
        delivery_slot: resolvedSlot,
        notes: finalNotes || rawOrder.notes,
        items: orderItems,
      });

      set((state) => ({
        orders:              [order, ...state.orders.filter(o => String(o.order_id ?? o.id) !== String(order.order_id ?? order.id))],
        current_order:       order,
        place_order_loading: false,
        place_order_error:   null,
      }));

      return {
        order,
        items:              orderItems,
        payment_session_id: json.data.payment_session_id ?? null,
        cashfree_order_id:  json.data.cashfree_order_id  ?? null,
        cf_order_status:    json.data.cf_order_status    ?? null,
      };

    } catch (err) {
      set({ place_order_error: err.message, place_order_loading: false });

      // Restore cart on failure (Buy Now only)
      if (buyNowItem && snapshotItems !== null) {
        try {
          await apiFetch('api/v1/customer/cart/clear', { method: 'DELETE' });
          for (const item of snapshotItems) {
            const p = { product_id: Number(item.product_id), quantity: Number(item.quantity ?? 1) };
            if (item.product_variant_id) p.product_variant_id = Number(item.product_variant_id);
            await apiFetch('api/v1/customer/cart/add', { method: 'POST', body: p });
          }
          get().fetchCart?.();
        } catch { /* best-effort */ }
      }

      throw err;
    }
  },

  // GET /api/v1/customer/orders/:id
  getOrderDetails: async (orderId) => {
    try {
      const cleanId = String(orderId).replace(/^#/, '').trim();
      const json    = await apiFetch(`api/v1/customer/orders/${cleanId}`);
      const rawData = json.data?.order ?? json.data ?? {};
      const order   = normalizeOrder(rawData);
      const items   = json.data?.items ?? json.data?.order?.items ?? rawData.items ?? rawData.order_items ?? [];
      const merged  = { ...order, items: items.length ? items : (order.items || []) };
      set({ current_order: merged });
      return merged;
    } catch (err) {
      console.error('getOrderDetails:', err);
      return null;
    }
  },

  // POST /api/v1/customer/orders/:id/cancel
  cancelOrder: async (orderId, remarks = 'Cancelled by customer') => {
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/cancel`, { method: 'POST', body: { remarks } });
      const cancelledData = json?.data?.order ?? json?.data;
      const normalizedCancelled = cancelledData && typeof cancelledData === 'object' ? normalizeOrder(cancelledData) : {};

      set((state) => ({
        orders: state.orders.map((o) =>
          String(o.order_id ?? o.id) === String(orderId)
            ? {
                ...normalizedCancelled,
                ...o,                           // original data wins for financial fields
                status: 'CANCELLED',
                order_status: 'CANCELLED',
                // Preserve totals explicitly so they're never wiped by a minimal API response
                total:        o.total        ?? o.total_amount    ?? normalizedCancelled.total        ?? 0,
                total_amount: o.total_amount ?? o.total           ?? normalizedCancelled.total_amount ?? 0,
                subtotal:     o.subtotal     ?? normalizedCancelled.subtotal     ?? 0,
                delivery_fee: o.delivery_fee ?? normalizedCancelled.delivery_fee ?? 0,
                tax_amount:   o.tax_amount   ?? normalizedCancelled.tax_amount   ?? 0,
              }
            : o
        ),
        current_order:
          state.current_order &&
          String(state.current_order.order_id ?? state.current_order.id) === String(orderId)
            ? {
                ...normalizedCancelled,
                ...state.current_order,
                status: 'CANCELLED',
                order_status: 'CANCELLED',
                total:        state.current_order.total        ?? state.current_order.total_amount    ?? normalizedCancelled.total        ?? 0,
                total_amount: state.current_order.total_amount ?? state.current_order.total           ?? normalizedCancelled.total_amount ?? 0,
                subtotal:     state.current_order.subtotal     ?? normalizedCancelled.subtotal     ?? 0,
                delivery_fee: state.current_order.delivery_fee ?? normalizedCancelled.delivery_fee ?? 0,
                tax_amount:   state.current_order.tax_amount   ?? normalizedCancelled.tax_amount   ?? 0,
              }
            : state.current_order,
        orders_loading: false,
      }));

      return json;
    } catch (err) {
      set({ orders_error: err.message, orders_loading: false });
      throw err;
    }
  },

  // GET /api/v1/customer/orders/:id/tracking
  fetchOrderTracking: async (orderId, { silent = false } = {}) => {
    if (!silent) {
      set({ tracking_loading: true, tracking_error: null, order_tracking: null });
    }
    try {
      const cleanId = String(orderId).replace(/^#/, '').trim();
      const json = await apiFetch(`api/v1/customer/orders/${cleanId}/tracking`);
      const tracking = json.data ?? json;
      set({ order_tracking: tracking, tracking_loading: false });
      return tracking;
    } catch (err) {
      if (!silent) {
        set({ tracking_error: err.message, tracking_loading: false });
      }
      return null;
    }
  },

  // POST /api/v1/customer/orders/:id/pay
  //
  // Strategy:
  //   1. Fetch order details — reuse payment_session_id if server stored one.
  //   2. If not found, call /pay to let the backend create a Cashfree order.
  //   3. If Cashfree rejects with "already present", the session was created
  //      previously but not persisted. Surface a typed error so the UI can
  //      call verifyPayment and tell the user what actually happened.
  retryPayment: async (orderId) => {
    // Step 1 — check if order details already carry a live session
    try {
      const detailJson = await apiFetch(`api/v1/customer/orders/${orderId}`);
      const orderData  = detailJson.data?.order ?? detailJson.data ?? {};
      const sid = orderData.payment_session_id ?? orderData.cashfree_session_id ?? null;
      const cid = orderData.cashfree_order_id  ?? orderData.cf_order_id         ?? null;
      if (sid) return { payment_session_id: sid, cashfree_order_id: cid };
    } catch { /* order-detail fetch failed — proceed to /pay */ }

    // Step 2 — ask backend to create / return a payment session
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/pay`, { method: 'POST' });
      return json.data;
    } catch (err) {
      // Step 3 — Cashfree says a session already exists for this order ID.
      // Attach a machine-readable flag so handleRetryPayment can run
      // verifyPayment instead of showing a raw error.
      const isDuplicate =
        err.message?.toLowerCase().includes('already present') ||
        err.message?.toLowerCase().includes('same id');

      if (isDuplicate) {
        const e = new Error(
          'A payment session already exists for this order. Checking payment status…'
        );
        e.code = 'CASHFREE_DUPLICATE';
        throw e;
      }

      throw err;
    }
  },

  // GET /api/v1/customer/orders/:id/verify-payment
  verifyPayment: async (orderId) => {
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/verify-payment`);
      const data = json.data ?? {};
      const orderData = data.order ?? data;
      const order = orderData?.order_id || orderData?.id ? normalizeOrder(orderData) : null;
      const payment_status = (
        data.payment_status ??
        data.paymentStatus ??
        order?.payment_status ??
        ''
      ).toUpperCase();

      if (order) {
        set((state) => ({
          current_order: order,
          orders: state.orders.map((item) =>
            String(item.order_id) === String(order.order_id) ? order : item
          ),
        }));
      } else if (payment_status) {
        set((state) => ({
          orders: state.orders.map((item) =>
            String(item.order_id) === String(orderId)
              ? { ...item, payment_status }
              : item
          ),
        }));
      }

      return { ...data, order, payment_status };
    } catch (err) {
      console.error('verifyPayment:', err);
      throw err;
    }
  },

  // ─── Reorder ─────────────────────────────────────────────────────────────
  // Fetches the order's items (if not already on the order object) and adds
  // each one back to the cart, SKIPPING items already present in the cart.
  // Returns { added, skipped, failed, total } for toast feedback.
  reorderItems: async (orderId) => {
    try {
      // 1. Try to find the order in local state first (items may already be there)
      let items = get().orders.find(
        (o) => String(o.order_id ?? o.id) === String(orderId)
      )?.items ?? [];

      // 2. If no items locally, fetch from the API
      if (!items.length) {
        const json = await apiFetch(`api/v1/customer/orders/${orderId}`);
        items = json.data?.items ?? json.data?.order?.items ?? [];
      }

      if (!items.length) {
        return { added: 0, skipped: 0, failed: 0, total: 0 };
      }

      // 3. Read current cart from Zustand store (already synced from server)
      const currentCart = get().cart ?? [];
      const addedProductIds = new Set();

      // Helper: returns true if this product is already in the cart in ANY form.
      // We match on product_id only — regardless of variant — so the same product
      // is never added twice even if the variant in the old order differs from what
      // is already in the cart (e.g. Apple variant-157 in cart vs. no-variant Apple
      // from the old order → both are the same Apple, skip).
      const isAlreadyInCart = (productId) => {
        if (addedProductIds.has(String(productId))) return true;
        return currentCart.some(
          (cartItem) => String(cartItem.product_id ?? cartItem.id) === String(productId)
        );
      };

      let added   = 0;
      let skipped = 0;
      let failed  = 0;

      for (const item of items) {
        const productId = item.product_id ?? item.id;
        const variantId = item.product_variant_id ?? item.variant_id ?? null;
        const quantity  = parseInt(item.quantity ?? item.qty ?? 1, 10);

        if (!productId) { failed++; continue; }

        // Skip if this product is already in the cart (any variant)
        if (isAlreadyInCart(productId)) {
          skipped++;
          continue;
        }

        try {
          const payload = {
            product_id: Number(productId),
            quantity:   Number(quantity),
          };
          if (variantId) payload.product_variant_id = Number(variantId);

          await apiFetch('api/v1/customer/cart/add', {
            method: 'POST',
            body: payload,
          });
          added++;
          addedProductIds.add(String(productId));
        } catch {
          failed++;
        }
      }

      // 4. Refresh the cart store only if something new was added
      if (added > 0) get().fetchCart?.();

      return { added, skipped, failed, total: items.length };
    } catch (err) {
      console.error('reorderItems:', err);
      return { added: 0, skipped: 0, failed: 0, total: 0, error: err.message };
    }
  },
});
